import { useState } from 'react'
import { useMemo } from 'react'

import {
  EmploymentNotification,
  EmploymentStatus,
  TokenManipulate,
  responseOffer,
  useCurrentUser,
  useRefreshCurrentUser,
} from '@builddocs/data-management'
import { useToast } from '@builddocs/system'
import { LoadingButton } from '@mui/lab'
import { Typography, Stack, Box, Link } from '@mui/material'
import { AxiosError } from 'axios'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import { RefetchOptions, RefetchQueryFilters } from 'react-query'

interface OwnProps {
  employment: EmploymentNotification
  refetch: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined,
  ) => void
}

export type HireItemProps = OwnProps

export const HireItem = (props: HireItemProps) => {
  const { employment, refetch } = props

  const fontWeight = useMemo(
    () => (employment.viewed ? 'initial' : 'bold'),
    [employment.viewed],
  )

  const { user } = useCurrentUser()
  const refreshCurrentUser = useRefreshCurrentUser()
  const { toast } = useToast()

  const [isLoadingItem, setIsLoadingItem] = useState<string>()

  const responseHirement = (employeeResponseType: EmploymentStatus) => {
    setIsLoadingItem(employeeResponseType)

    return responseOffer(
      {
        businessKey: employment.businessKey,
        senderName: employment.senderName,
        receiverName: employment.receiverName,
        senderId: employment.senderId,
        receiverId: employment.receiverId,
        senderEmail: employment.senderEmail,
        receiverEmail: employment.receiverEmail,
        positionName: employment.positionName,
        orderName: employment.orderName,
        orderDate: employment.orderDate,
      },
      employeeResponseType,
    )
      .then(() => toast.success('Действие подтверждено'))
      .catch(e => {
        const error = e as AxiosError

        toast.error(
          error?.response?.data?.message ?? 'Ошибка при подтверждении действия',
        )
      })
      .finally(() => {
        refetch()
        setIsLoadingItem(undefined)
      })
  }

  /** Уведомление о найме. Информация о статусе для отправителя */
  if (employment.solved)
    return (
      <Stack direction="column">
        <Box>
          <Typography sx={{ display: 'inline', fontWeight }} variant="body1">
            Приглашение{' '}
            {employment.status === EmploymentStatus.PENDING && 'от '}
          </Typography>
          <Link
            sx={{ display: 'inline', fontWeight }}
            underline="hover"
            variant="body1"
          >
            {employment.senderName}
          </Link>
          <Typography sx={{ display: 'inline', fontWeight }} variant="body1">
            {' '}
            на позицию{' '}
          </Typography>
          <Link
            sx={{ display: 'inline', fontWeight }}
            underline="hover"
            variant="body1"
          >
            {employment.positionName}
          </Link>
          {employment.orderName && employment.orderDate && (
            <Typography sx={{ display: 'inline', fontWeight }} variant="body1">
              {` по приказу № ${employment.orderName} от ${format(
                new Date(employment.orderDate),
                'P',
                { locale: ru },
              )}`}
            </Typography>
          )}
          <Typography sx={{ display: 'inline', fontWeight }} variant="body1">
            {employment.status === EmploymentStatus.PENDING && ' обработано'}
            {employment.status === EmploymentStatus.ACCEPTED && ' принято'}
            {employment.status === EmploymentStatus.REJECTED && ' отклонено'}
          </Typography>
        </Box>
      </Stack>
    )

  switch (employment.status) {
    /** Уведомление о найме в ожидании. Ожидает выбора */
    case EmploymentStatus.PENDING:
      return (
        <Stack direction="column">
          <Box>
            <Link
              sx={{ display: 'inline', fontWeight }}
              underline="hover"
              variant="body1"
            >
              {employment.senderName}
            </Link>
            <Typography sx={{ display: 'inline', fontWeight }} variant="body1">
              {' '}
              приглашает вас на должность{' '}
            </Typography>
            <Link
              sx={{ display: 'inline', fontWeight }}
              underline="hover"
              variant="body1"
            >
              {employment.positionName}
            </Link>
            {employment.orderName && employment.orderDate && (
              <Typography
                sx={{ display: 'inline', fontWeight }}
                variant="body1"
              >
                {` по приказу № ${employment.orderName} от ${format(
                  new Date(employment.orderDate),
                  'P',
                  { locale: ru },
                )}`}
              </Typography>
            )}
          </Box>
          <Stack direction="row" gap={1} mt={1}>
            <LoadingButton
              color="success"
              disabled={!!user.employment || isLoadingItem}
              loading={isLoadingItem === EmploymentStatus.ACCEPTED}
              variant="contained"
              onClick={() =>
                responseHirement(EmploymentStatus.ACCEPTED).then(() => {
                  TokenManipulate.refresh(
                    localStorage.getItem('token') as string,
                  ).then(async () => await refreshCurrentUser())
                })
              }
              onMouseDown={event => event.stopPropagation()}
            >
              Принять
            </LoadingButton>
            <LoadingButton
              color="error"
              disabled={!!user.employment || isLoadingItem}
              loading={isLoadingItem === EmploymentStatus.REJECTED}
              variant="contained"
              onClick={() => responseHirement(EmploymentStatus.REJECTED)}
              onMouseDown={event => event.stopPropagation()}
            >
              Отклонить
            </LoadingButton>
          </Stack>
        </Stack>
      )
    /** Уведомление о найме принято */
    case EmploymentStatus.ACCEPTED:
      return (
        <Stack direction="column">
          <Box>
            <Link
              sx={{ display: 'inline', fontWeight }}
              underline="hover"
              variant="body1"
            >
              {employment.senderName}
            </Link>
            <Typography sx={{ display: 'inline', fontWeight }} variant="body1">
              {' '}
              принял приглашение на должность{' '}
            </Typography>
            <Link
              sx={{ display: 'inline', fontWeight }}
              underline="hover"
              variant="body1"
            >
              {employment.positionName}
            </Link>
          </Box>
        </Stack>
      )
    /** Уведомление о найме отклонено */
    case EmploymentStatus.REJECTED:
      return (
        <Stack direction="column">
          <Box>
            <Link
              sx={{ display: 'inline', fontWeight }}
              underline="hover"
              variant="body1"
            >
              {employment.senderName}
            </Link>
            <Typography sx={{ display: 'inline', fontWeight }} variant="body1">
              {' '}
              не принял приглашение на должность{' '}
            </Typography>
            <Link
              sx={{ display: 'inline', fontWeight }}
              underline="hover"
              variant="body1"
            >
              {employment.positionName}
            </Link>
          </Box>
        </Stack>
      )
    default:
      return null
  }
}
